import { AxiosError, bookingAxios, getPointOfSaleFromLocation } from 'common';
import {
  AwardCart,
  AwardError,
  AwardFlightsQueryParams,
  AwardFlightsResponse,
  AwardJourneyRequestBody,
  AwardLocationsQueryParams,
  AwardLocationsResponse,
  AwardOrder,
  AwardOrderRequestBody,
  AwardOrderStatus,
  AwardPassengerRequestBody,
  AwardRequestHeaders,
  AwardSendOneTimePasswordResponse,
  BonusProgramItem,
  FarePriceErrorType,
  OneTimePasswordErrorType,
  RequestHeaders,
  stringToEnum,
} from 'shared';

import { API_URL } from './constants';
import { FarePriceError, OneTimePasswordError, PassengersFieldError } from './errors';
import { getAuthorization } from './utils';

bookingAxios.defaults.withCredentials = true;

function getDefaultHeaders(): Omit<AwardRequestHeaders, keyof Omit<RequestHeaders, 'accept-language'>> {
  return {
    pos: getPointOfSaleFromLocation(location),
    authorization: getAuthorization(),
  };
}

export async function fetchLocations(params: AwardLocationsQueryParams): Promise<AwardLocationsResponse> {
  const { data } = await bookingAxios.get(`${API_URL}/locations`, {
    headers: getDefaultHeaders(),
    params,
  });

  return data;
}

export async function fetchFrequentFlyerProgram(): Promise<BonusProgramItem[]> {
  const { data } = await bookingAxios.get(`${API_URL}/frequent-flyer-programs`, {
    headers: getDefaultHeaders(),
  });

  return data;
}

export async function fetchBooking(bookingId: string): Promise<BonusProgramItem[]> {
  const { data } = await bookingAxios.get(`${API_URL}/bookings/${bookingId}`, {
    headers: getDefaultHeaders(),
  });

  return data;
}

export async function fetchOrderStatus(orderId: string): Promise<AwardOrderStatus> {
  const { data } = await bookingAxios.get(`${API_URL}/orders/${orderId}/status`, {
    headers: getDefaultHeaders(),
  });

  return data;
}

export async function fetchFlights(params: AwardFlightsQueryParams): Promise<AwardFlightsResponse> {
  const { data } = await bookingAxios.get(`${API_URL}/flights`, {
    params,
    headers: getDefaultHeaders(),
  });

  return data;
}

export async function createCart(): Promise<AwardCart> {
  const { data } = await bookingAxios.post(`${API_URL}/carts`, null, {
    headers: getDefaultHeaders(),
  });

  return data;
}

export async function addJourneyToCart({
  cartId,
  payload,
}: {
  cartId: string;
  payload: AwardJourneyRequestBody;
}): Promise<AwardCart> {
  const { data } = await bookingAxios.post(`${API_URL}/carts/${cartId}/journey`, payload, {
    headers: getDefaultHeaders(),
  });

  return data;
}

export async function addPassengersToCart({
  cartId,
  passengers,
}: {
  cartId: string;
  passengers: AwardPassengerRequestBody;
}): Promise<AwardCart> {
  try {
    const { data } = await bookingAxios.post(`${API_URL}/carts/${cartId}/passengers`, passengers, {
      headers: getDefaultHeaders(),
    });

    return data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const type = error.response?.data.bookingErrorType;

      if (type === AwardError.PASSENGER_FIELD_ERRORS) {
        const payload = error.response?.data.payload;
        throw new PassengersFieldError(payload);
      }
    }
    throw error;
  }
}

export async function createOrder(body: AwardOrderRequestBody): Promise<AwardOrder> {
  try {
    const { data } = await bookingAxios.post(`${API_URL}/orders`, body, {
      headers: getDefaultHeaders(),
    });

    return data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const type = stringToEnum(FarePriceErrorType, error.response?.data.bookingErrorType);

      if (type) {
        throw new FarePriceError(type);
      }
    }
    throw error;
  }
}

export async function sendOneTimePassword(orderId: string): Promise<AwardSendOneTimePasswordResponse> {
  const { data } = await bookingAxios.post(
    `${API_URL}/otp/send`,
    { orderId },
    {
      headers: getDefaultHeaders(),
    }
  );

  return data;
}

export async function verifyOneTimePassword(orderId: string, code: string): Promise<void> {
  try {
    const response = await bookingAxios.post(
      `${API_URL}/otp/verify`,
      { orderId, code },
      {
        headers: getDefaultHeaders(),
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const type = stringToEnum(OneTimePasswordErrorType, error.response?.data.bookingErrorType);

      if (type) {
        throw new OneTimePasswordError(type);
      }
    }

    throw error;
  }
}

export async function extendSession(sessionId?: string): Promise<{ sessionId: string }> {
  const { data } = await bookingAxios.post(
    `${API_URL}/session/extend`,
    { sessionId },
    { headers: getDefaultHeaders() }
  );

  return data;
}
